import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useWallet, useAnchorWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import { toast } from 'react-hot-toast'
import {
  RefreshCw,
  ArrowLeft,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Copy,
  Users,
  TrendingUp,
  Vote
} from 'lucide-react'
import BN from 'bn.js'

import {
  Pool,
  Donor,
  Proposal,
  VoteRecord,
  VoteChoice,
  CtoPoolsClient,
  createCtoPoolsClient,
  ContractStatus
} from '../lib/anchorClient'
import { createConnection, SolanaErrorUtils } from '../lib/solana'
import { cn, A11yUtils } from '../lib/utils'
import {
  transformPoolForOverview,
  transformDonorsForTable,
  transformProposalForGovernance,
  calculateUserVotingPower,
  canCreateProposal,
  getWithdrawableAmount,
} from '../lib/poolDataTransformers'

// Component imports
// Layout is provided by App.tsx - no need to import here
import PoolOverview from '../components/PoolOverview'
import WalletPanel from '../components/WalletPanel'
import GovernancePanel from '../components/GovernancePanel'
import ProposalHistory from '../components/ProposalHistory'
import DonorTable from '../components/DonorTable'
import DonateModal from '../components/modals/DonateModal'
import WithdrawModal from '../components/modals/WithdrawModal'
import CreateProposalModal from '../components/modals/CreateProposalModal'

/**
 * Interface for pool data state management
 * Organizes all pool-related data with loading and error states
 */
interface PoolDataState {
  pool: Pool | null
  userDonor: Donor | null
  activeProposal: Proposal | null
  userVoteRecord: VoteRecord | null
  allDonors: Array<{ account: Donor; publicKey: PublicKey }>
  proposalHistory: Array<{ account: Proposal; publicKey: PublicKey }>
  loading: boolean
  error: string | null
  lastFetched: number | null
  contractStatus: ContractStatus | null
}

/**
 * Interface for modal states
 * Manages visibility of all modal components
 */
interface ModalStates {
  donate: boolean
  withdraw: boolean
  createProposal: boolean
}

/**
 * PoolPage Component - Main pool dashboard combining all functionality
 * Provides comprehensive pool management interface with data fetching and state management
 * Integrates all components for donations, withdrawals, governance, and history
 * 
 * Layout Structure (Reformatted 2026-01-02):
 * - Header with navigation and pool info
 * - Token tracking links
 * - Two-column responsive grid:
 *   - Left Column (2/3): Pool Overview, Donor Table
 *   - Right Column (1/3): Wallet Panel, Pool Actions, Governance Panel
 * 
 * V3.1 Updates (2026-01-23):
 * - Added abort voting functionality
 * - Added handleVoteAbort and handleAbortProposal handlers
 * - Updated voting power calculation to include strike counts
 */
export function PoolPage(): JSX.Element {
  const { tokenMint } = useParams<{ tokenMint: string }>()
  const navigate = useNavigate()
  const { connected, publicKey } = useWallet()
  const anchorWallet = useAnchorWallet()

  // State management
  const [poolData, setPoolData] = useState<PoolDataState>({
    pool: null,
    userDonor: null,
    activeProposal: null,
    userVoteRecord: null,
    allDonors: [],
    proposalHistory: [],
    loading: false,
    error: null,
    lastFetched: null,
    contractStatus: null
  })

  const [modals, setModals] = useState<ModalStates>({
    donate: false,
    withdraw: false,
    createProposal: false
  })

  const [refreshing, setRefreshing] = useState(false)
  const [client, setClient] = useState<CtoPoolsClient | null>(null)

  const connection = useMemo(() => createConnection('devnet'), [])

  /**
   * Initialize the CTO Pools client when wallet connects
   * Creates the client instance for blockchain interactions
   */
  useEffect(() => {
    if (connected && anchorWallet) {
      console.log('🔍 PoolPage: Initializing client with anchorWallet')
      console.log('🔍 PoolPage: anchorWallet object:', anchorWallet)
      console.log('🔍 PoolPage: signTransaction is:', anchorWallet.signTransaction)
      
      // We need to cast anchorWallet to any because createCtoPoolsClient expects WalletContextState
      // but anchorWallet is a simpler interface that is compatible with AnchorProvider
      const newClient = createCtoPoolsClient(connection, anchorWallet as any)
      setClient(newClient)
    } else {
      setClient(null)
    }
  }, [connected, anchorWallet, connection])

  /**
   * Validate token mint parameter
   * Ensures valid token mint address from URL
   */
  const validateTokenMint = (): PublicKey | null => {
    if (!tokenMint) {
      setPoolData(prev => ({ ...prev, error: 'No token mint specified in URL' }))
      return null
    }

    try {
      return new PublicKey(tokenMint)
    } catch (err) {
      setPoolData(prev => ({ ...prev, error: 'Invalid token mint address' }))
      return null
    }
  }

  /**
   * Fetch comprehensive pool data
   * Loads all pool-related information including proposals, donors, and user data
   */
  const fetchPoolData = useCallback(async (showLoadingState = true) => {
    const tokenMintPubkey = validateTokenMint()
    if (!tokenMintPubkey) return

    if (showLoadingState) {
      setPoolData(prev => ({ ...prev, loading: true, error: null }))
    }

    try {
      // Create a basic client for data fetching (even without wallet)
      const basicWallet = {
        publicKey: null,
        signTransaction: null,
        signAllTransactions: null,
        signMessage: null,
        connecting: false,
        connected: false,
        disconnecting: false,
        wallet: null,
        autoConnect: false,
        wallets: [],
        select: () => {},
        signIn: null,
        connect: async () => {},
        disconnect: async () => {},
        sendTransaction: async () => ''
      }

      const dataClient = new CtoPoolsClient(connection, basicWallet as any)

      // Check contract deployment status first
      const contractStatus = await dataClient.checkContractDeployment()

      // Fetch pool data
      const pool = await dataClient.fetchPool(tokenMintPubkey)
      if (!pool) {
        setPoolData(prev => ({ ...prev, error: 'Pool not found for this token mint', loading: false }))
        return
      }

      // Get pool PDA for donors and proposals
      const [poolPDA] = dataClient.getPoolPDA(tokenMintPubkey)

      // Fetch all donors
      const allDonors = await dataClient.getPoolDonors(poolPDA)

      // Fetch user donor if connected
      let userDonor: Donor | null = null
      if (connected && publicKey) {
        userDonor = await dataClient.fetchDonor(poolPDA, publicKey)
      }

      // Fetch active proposal if exists
      let activeProposal: Proposal | null = null
      let userVoteRecord: VoteRecord | null = null
      
      if (pool.activeProposal && !pool.activeProposal.equals(PublicKey.default)) {
        activeProposal = await dataClient.fetchProposal(poolPDA)
        
        // Fetch user vote record if user is connected and proposal exists
        if (connected && publicKey && activeProposal && pool.activeProposal) {
          userVoteRecord = await dataClient.fetchVoteRecord(pool.activeProposal, publicKey)
        }
      }

      // For now, we'll leave proposal history empty as we don't have historical data
      // In a real implementation, you'd need to store and fetch historical proposals
      const proposalHistory: Array<{ account: Proposal; publicKey: PublicKey }> = []

      setPoolData({
        pool,
        userDonor,
        activeProposal,
        userVoteRecord,
        allDonors,
        proposalHistory,
        loading: false,
        error: null,
        lastFetched: Date.now(),
        contractStatus
      })

    } catch (err: any) {
      console.error('Failed to fetch pool data:', err)
      setPoolData(prev => ({
        ...prev,
        loading: false,
        error: SolanaErrorUtils.parseTransactionError(err),
        contractStatus: prev.contractStatus
      }))
    }
  }, [tokenMint, connected, publicKey, connection])

  /**
   * Initial data fetch on component mount
   * Loads pool data when component first renders
   */
  useEffect(() => {
    fetchPoolData()
  }, [fetchPoolData])

  /**
   * Handle refresh with user feedback
   * Provides visual feedback during data refresh
   */
  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await fetchPoolData(false)
      toast.success('Pool data refreshed')
    } catch (err) {
      toast.error('Failed to refresh pool data')
    } finally {
      setRefreshing(false)
    }
  }

  /**
   * Handle donation transaction
   * Processes SOL donations to the pool
   */
  const handleDonate = async (amount: BN) => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      await client.donate(tokenMintPubkey, amount)
      
      toast.success('Donation successful!', {
        duration: 5000,
      })
      
      A11yUtils.announceToScreenReader('Donation completed successfully')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * Handle withdrawal transaction
   * Processes SOL withdrawals from the pool
   */
  const handleWithdraw = async (amount?: BN) => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      await client.withdraw(tokenMintPubkey, amount)
      
      toast.success(amount ? 'Partial withdrawal successful!' : 'Full withdrawal successful!', {
        duration: 5000,
      })
      
      A11yUtils.announceToScreenReader('Withdrawal completed successfully')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * Handle proposal creation
   * Creates new funding proposals for community voting
   * V2: Now includes title and description parameters
   */
  const handleCreateProposal = async (title: string, description: string, requestedAmount: BN, destinationAddress: string) => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    // V2: Parse destination wallet address
    let destinationWallet: PublicKey
    try {
      destinationWallet = new PublicKey(destinationAddress)
    } catch (e) {
      if (publicKey) {
        destinationWallet = publicKey
        toast('Invalid destination address, defaulting to your wallet', { icon: '⚠️' })
      } else {
        toast.error('Invalid destination address')
        return
      }
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      // V2: createProposal now takes title and description
      await client.createProposal(tokenMintPubkey, requestedAmount, destinationWallet, title, description)
      
      toast.success('Proposal created successfully!', {
        duration: 5000,
      })
      
      A11yUtils.announceToScreenReader('Proposal created and voting has begun')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * Handle voting on proposals
   * Processes votes on active proposals (Yes, No, Abstain)
   */
  const handleVote = async (_proposalId: string, vote: 'for' | 'against' | 'abstain') => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    // Map vote string to VoteChoice enum
    let choice: VoteChoice
    switch (vote) {
      case 'for':
        choice = VoteChoice.Yes
        break
      case 'against':
        choice = VoteChoice.No
        break
      case 'abstain':
        choice = VoteChoice.Abstain
        break
      default:
        choice = VoteChoice.Yes
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      await client.vote(tokenMintPubkey, choice)
      
      toast.success(`Vote cast: ${vote}`, {
        duration: 5000,
      })
      
      A11yUtils.announceToScreenReader(`Vote for ${vote} recorded successfully`)
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * V3.1: Handle abort vote on proposals
   * Allows donors to vote to abort an active proposal
   * Two abort votes are required to fully abort a proposal
   */
  const handleVoteAbort = async (_proposalId: string) => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      await client.vote(tokenMintPubkey, VoteChoice.Abort)
      
      toast.success('Abort vote cast successfully', {
        duration: 5000,
        icon: '⚠️'
      })
      
      A11yUtils.announceToScreenReader('Abort vote recorded. Warning: If proposal passes, you may receive a strike.')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * V3.1: Handle proposal abortion finalization
   * Called after 2 wallets have voted abort to finalize the proposal abortion
   * Unlocks reserved liquidity and marks proposal as Aborted
   */
  const handleAbortProposal = async (_proposalId: string) => {
    if (!client || !tokenMint) {
      toast.error('Wallet not connected or pool not found')
      return
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      await client.abortProposal(tokenMintPubkey)
      
      toast.success('Proposal aborted successfully!', {
        duration: 5000,
        icon: '🛑'
      })
      
      A11yUtils.announceToScreenReader('Proposal has been aborted. Reserved funds have been unlocked.')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * Handle proposal execution
   * Executes proposals after voting completion
   * V2: Simplified signature - accounts are derived from pool state
   */
  const handleExecuteProposal = async () => {
    if (!client || !tokenMint || !poolData.activeProposal || !poolData.pool) {
      toast.error('Cannot execute proposal at this time')
      return
    }

    try {
      const tokenMintPubkey = new PublicKey(tokenMint)
      // V2: executeProposal now only takes tokenMint and optional minimumLamportsOut
      await client.executeProposal(tokenMintPubkey)
      
      toast.success('Proposal executed successfully!', {
        duration: 5000,
      })
      
      A11yUtils.announceToScreenReader('Proposal execution completed')
      
      // Refresh data after successful transaction
      await fetchPoolData(false)
    } catch (err: any) {
      const errorMessage = SolanaErrorUtils.parseTransactionError(err)
      toast.error(errorMessage)
      throw err
    }
  }

  /**
   * Modal control handlers
   * Manage visibility of modal components
   */
  const openModal = (modal: keyof ModalStates) => {
    setModals(prev => ({ ...prev, [modal]: true }))
  }

  const closeModal = (modal: keyof ModalStates) => {
    setModals(prev => ({ ...prev, [modal]: false }))
  }

  // Error state - pool not found or invalid
  // Note: Layout wrapper removed - App.tsx already provides Layout for all routes
  if (poolData.error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="cyber-card p-8">
            <AlertTriangle className="w-16 h-16 text-status-error mx-auto mb-4" />
            <h1 className="font-header text-2xl text-status-error mb-4">Pool Not Found</h1>
            <p className="text-text-secondary mb-6">{poolData.error}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="cyber-button-primary flex items-center space-x-2 px-6 py-3"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </button>
              <button
                onClick={() => fetchPoolData()}
                className="cyber-button flex items-center space-x-2 px-6 py-3"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Loading state
  // Note: Layout wrapper removed - App.tsx already provides Layout for all routes
  if (poolData.loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="cyber-card p-8">
            <Loader2 className="w-16 h-16 text-cyber-cyan mx-auto mb-4 animate-spin" />
            <h1 className="font-header text-2xl text-cyber-cyan mb-4">Loading Pool Data</h1>
            <p className="text-text-secondary">Please wait while we fetch the latest pool information...</p>
          </div>
        </div>
      </div>
    )
  }

  const tokenMintPubkey = validateTokenMint()
  if (!tokenMintPubkey) return <></> // This case is handled by error state above

  // Transform data for components
  const poolStats = poolData.pool
    ? transformPoolForOverview(poolData.pool, poolData.allDonors.length, poolData.activeProposal)
    : null

  const contributors = poolData.pool
    ? transformDonorsForTable(poolData.allDonors, poolData.pool)
    : []

  const activeProposalUI = poolData.activeProposal && poolData.pool && poolData.pool.activeProposal
    ? [transformProposalForGovernance(poolData.activeProposal, poolData.pool, poolData.pool.activeProposal)]
    : []

  // V3.1: Updated voting power calculation includes strike counts
  const votingPower = poolData.pool
    ? calculateUserVotingPower(poolData.userDonor, poolData.pool, poolData.allDonors)
    : { 
        totalShares: 0, 
        votingWeight: 0, 
        delegatedVotes: 0, 
        participationRate: 0, 
        rank: 0, 
        totalVotedProposals: 0,
        // V3.1: Strike tracking defaults
        abortStrikeCount: 0,
        proposeStrikeCount: 0
      }

  // V3.1: Updated user votes to support abort vote type
  const userVotes: Record<string, 'for' | 'against' | 'abstain' | 'abort'> = poolData.userVoteRecord && poolData.pool?.activeProposal
    ? { 
        [poolData.pool.activeProposal.toBase58()]: 
          'yes' in poolData.userVoteRecord.choice ? 'for' :
          'no' in poolData.userVoteRecord.choice ? 'against' :
          'abort' in poolData.userVoteRecord.choice ? 'abort' :
          'abstain'
      }
    : {}

  // Token tracking links
  const trackingLinks = [
    { name: 'Photon', url: `https://photon-sol.tinyastro.io/en/lp/${tokenMint}`, icon: '⚡' },
    { name: 'GMGN', url: `https://gmgn.ai/sol/token/${tokenMint}`, icon: '🤖' },
    { name: 'DexTools', url: `https://www.dextools.io/app/en/solana/pair-explorer/${tokenMint}`, icon: '🛠️' },
    { name: 'DexScreener', url: `https://dexscreener.com/solana/${tokenMint}`, icon: '📊' },
    { name: 'BullX', url: `https://bullx.io/terminal?chainId=1399811149&address=${tokenMint}`, icon: '🐂' },
    { name: 'Birdeye', url: `https://birdeye.so/token/${tokenMint}?chain=solana`, icon: '🦅' },
  ]

  // Note: Layout wrapper removed - App.tsx already provides Layout for all routes
  return (
    <div className="space-y-6 lg:space-y-8">
        {/* ========== HEADER SECTION ========== */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/')}
              className="cyber-button p-2 flex-shrink-0"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="font-header text-2xl sm:text-3xl text-cyber-cyan text-glow truncate">
                Pool Dashboard
              </h1>
              <p className="text-text-secondary text-sm sm:text-base">
                Community takeover pool management
                {poolData.contractStatus?.fallbackMode && (
                  <span className="text-status-warning ml-2">(Demo Mode)</span>
                )}
              </p>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(
              "cyber-button flex items-center space-x-2 px-3 sm:px-4 py-2",
              "hover:scale-105 transition-transform duration-200",
              "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
              "flex-shrink-0"
            )}
            aria-label="Refresh pool data"
          >
            <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
            <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </header>

        {/* ========== POOL & TOKEN INFO SECTION ========== */}
        <section className="glass-card p-4 sm:p-6">
          {/* Address Display */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-text-muted">Pool:</span>
              <code className="font-mono bg-dark-surface px-2 py-1 rounded flex items-center space-x-1">
                <span>{poolData.pool ? client?.getPoolPDA(tokenMintPubkey)[0].toBase58().slice(0, 8) + '...' : 'Loading...'}</span>
                <button
                  onClick={() => {
                    if (poolData.pool && client) {
                      navigator.clipboard.writeText(client.getPoolPDA(tokenMintPubkey)[0].toBase58())
                      toast.success('Pool address copied')
                    }
                  }}
                  className="hover:text-cyber-cyan transition-colors"
                  aria-label="Copy pool address"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </code>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-text-muted">Token:</span>
              <code className="font-mono bg-dark-surface px-2 py-1 rounded flex items-center space-x-1">
                <span>{tokenMint?.slice(0, 8)}...</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(tokenMint || '')
                    toast.success('Token address copied')
                  }}
                  className="hover:text-cyber-cyan transition-colors"
                  aria-label="Copy token address"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </code>
            </div>
          </div>
          
          {/* Token Tracking Links */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Track Token</h3>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {trackingLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-3 py-2 bg-dark-surface/50 hover:bg-dark-surface rounded-cyber border border-dark-border/30 hover:border-cyber-cyan/30 transition-all duration-200 group"
                >
                  <span className="text-lg">{link.icon}</span>
                  <span className="text-sm text-text-primary group-hover:text-cyber-cyan">{link.name}</span>
                  <ExternalLink className="w-3 h-3 text-text-muted group-hover:text-cyber-cyan" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ========== MAIN CONTENT GRID ========== 
             Reorganized layout for better visual hierarchy:
             - Left: Pool Overview (main data), Governance (for engaged users), Donors
             - Right: Wallet & Actions (sticky for easy access)
        */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          
          {/* ===== LEFT COLUMN - Pool Data & Community ===== */}
          <div className="lg:col-span-2 space-y-6 lg:space-y-8">
            
            {/* Pool Overview - Primary Information */}
            {poolStats && (
              <section aria-labelledby="pool-overview-heading">
                <PoolOverview
                  tokenMint={tokenMintPubkey.toBase58()}
                  poolStats={poolStats}
                  contributors={contributors}
                  showDetailedStats={true}
                  onRefresh={handleRefresh}
                  isRefreshing={refreshing}
                />
              </section>
            )}

            {/* Governance Panel - Voting & Proposals 
                V3.1: Now includes abort voting functionality */}
            <section aria-labelledby="governance-heading">
              <GovernancePanel
                proposals={activeProposalUI}
                votingPower={votingPower}
                userVotes={userVotes}
                loading={poolData.loading}
                onVote={handleVote}
                onVoteAbort={handleVoteAbort}        // V3.1: New abort vote handler
                onCreateProposal={() => openModal('createProposal')}
                onViewProposal={(id) => console.log('View proposal', id)}
                onExecuteProposal={handleExecuteProposal}
                onAbortProposal={handleAbortProposal}  // V3.1: Finalize abort handler
                userWallet={publicKey?.toBase58()}     // V3.1: Pass user wallet for abort eligibility
              />
            </section>

            {/* Proposal History - Past Proposals */}
            {poolData.proposalHistory.length > 0 && (
              <section aria-labelledby="proposal-history-heading">
                <ProposalHistory
                  proposals={poolData.proposalHistory}
                  totalShares={poolData.pool?.totalShares || new BN(0)}
                  loading={false}
                  error={null}
                  onRefresh={handleRefresh}
                />
              </section>
            )}

            {/* Donor Table - Community Members */}
            <section aria-labelledby="donors-heading">
              <DonorTable
                donors={contributors}
                loading={poolData.loading}
                showSearch={true}
                showFilters={true}
                showPagination={true}
                pageSize={10}
                refreshable={true}
                onRefresh={handleRefresh}
              />
            </section>
          </div>

          {/* ===== RIGHT COLUMN - Wallet & Actions =====
               UI FIX (2026-01-13): Fixed sidebar height to match viewport
               - Uses sticky positioning with top offset for navbar
               - max-h-[calc(100vh-6rem)] ensures it fits within viewport (minus navbar height)
               - overflow-y-auto enables independent scrolling for sidebar content
               - scrollbar-thin for styled, minimal scrollbar appearance
          */}
          <aside className="space-y-6 lg:space-y-8 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:scrollbar-thin lg:scrollbar-thumb-dark-border lg:scrollbar-track-transparent lg:pr-1">
            
            {/* Wallet Panel - Connection & Balance */}
            <section aria-labelledby="wallet-heading">
              <WalletPanel
                showBalanceValue={true}
                autoRefresh={true}
                refreshInterval={30000}
              />
            </section>
            
            {/* Pool Actions Card */}
            <section aria-labelledby="actions-heading" className="glass-card p-6 space-y-4">
              <h3 id="actions-heading" className="text-xl font-header text-gradient-cyber flex items-center space-x-2">
                <TrendingUp className="w-5 h-5" />
                <span>Pool Actions</span>
              </h3>
              
              <div className="grid gap-3">
                {/* Donate Button */}
                <button
                  onClick={() => openModal('donate')}
                  className="cyber-button-primary w-full py-3 flex items-center justify-center space-x-2"
                >
                  <span>Donate to Pool</span>
                </button>
                
                {/* Withdraw Button - Only show if user has donated */}
                {poolData.userDonor && (
                  <button
                    onClick={() => openModal('withdraw')}
                    className="cyber-button-secondary w-full py-3 flex items-center justify-center space-x-2"
                  >
                    <span>Withdraw Funds</span>
                  </button>
                )}
                
                {/* Create Proposal Button */}
                <button
                  onClick={() => {
                    const eligibility = canCreateProposal(poolData.userDonor)
                    if (eligibility.eligible) {
                      openModal('createProposal')
                    } else {
                      toast.error(eligibility.reason)
                    }
                  }}
                  className="cyber-button w-full py-3 flex items-center justify-center space-x-2"
                  disabled={!poolData.userDonor}
                >
                  <Vote className="w-4 h-4" />
                  <span>Create Proposal</span>
                </button>
              </div>
              
              {/* Pool Summary Stats */}
              {poolData.pool && (
                <div className="mt-4 pt-4 border-t border-dark-border/30">
                  <h4 className="text-sm font-medium text-text-muted mb-3">Pool Summary</h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-text-muted">Total Pool Value:</span>
                      <span className="text-text-primary font-mono font-medium">
                        {(poolData.pool.totalPoolTokens.toNumber() / 1e9).toFixed(4)} jitoSOL
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-muted">Contributors:</span>
                      <span className="text-text-primary font-mono flex items-center space-x-1">
                        <Users className="w-3 h-3" />
                        <span>{poolData.allDonors.length}</span>
                      </span>
                    </div>
                    {poolData.userDonor && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-text-muted">Your Shares:</span>
                          <span className="text-cyber-cyan font-mono font-medium">
                            {poolData.userDonor.shares.toNumber().toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-text-muted">Your Value:</span>
                          <span className="text-cyber-green font-mono font-medium">
                            {getWithdrawableAmount(poolData.userDonor, poolData.pool).toFixed(4)} SOL
                          </span>
                        </div>
                        {/* V3.1: Display strike counts if any */}
                        {(poolData.userDonor.abortStrikeCount > 0 || poolData.userDonor.proposeStrikeCount > 0) && (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-text-muted">Abort Strikes:</span>
                              <span className={cn(
                                "font-mono font-medium",
                                poolData.userDonor.abortStrikeCount >= 2 ? "text-status-error" :
                                poolData.userDonor.abortStrikeCount >= 1 ? "text-cyber-yellow" : "text-text-primary"
                              )}>
                                {poolData.userDonor.abortStrikeCount}/3
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-text-muted">Propose Strikes:</span>
                              <span className={cn(
                                "font-mono font-medium",
                                poolData.userDonor.proposeStrikeCount >= 2 ? "text-status-error" :
                                poolData.userDonor.proposeStrikeCount >= 1 ? "text-cyber-yellow" : "text-text-primary"
                              )}>
                                {poolData.userDonor.proposeStrikeCount}/3
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </section>
          </aside>
        </div>

        {/* ========== MODALS ========== */}
        <DonateModal
          isOpen={modals.donate}
          onClose={() => closeModal('donate')}
          onDonate={handleDonate}
          tokenSymbol="SOL"
        />

        <WithdrawModal
          isOpen={modals.withdraw}
          onClose={() => closeModal('withdraw')}
          onWithdraw={handleWithdraw}
          maxAmount={poolData.userDonor && poolData.pool ? getWithdrawableAmount(poolData.userDonor, poolData.pool) : 0}
          tokenSymbol="SOL"
        />

        <CreateProposalModal
          isOpen={modals.createProposal}
          onClose={() => closeModal('createProposal')}
          onCreateProposal={handleCreateProposal}
          tokenSymbol="SOL"
        />
    </div>
  )
}

export default PoolPage
